import { Button, Card, Space, Typography } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import "./GameNotificationPopup.scss";
import fifa from "../../../assets/images/fifa.svg";
import { postApiRequest } from "../../../services/postApiRequest";
import { END_POINTS } from "../../../Helper/Constant";
import { hideLoader, showLoader } from "../../../features/loader/loaderSlice";
import { showToast } from "../../../shared/sharedComponents/ToasterMessage/ToasterMessage";
import { getLocalStorageData } from "../../../shared/commonFunction";
import useJoinRequestSync from "../../../hooks/useJoinRequestSync";
import {
    removeJoinRequestById,
    selectJoinRequests,
} from "../../../features/duelsJoinRequest/joinRequestSlice";
import { selectDuelsActiveMatch } from "../../../features/duelsActiveMatch/activeMatchSlice";
import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useSocket from "../../../hooks/useSocket";

const { Text, Title } = Typography;

const normalizeActiveMatchPayload = (payload) => {
    if (!payload || typeof payload !== "object") return null;
    if ("room" in payload || "isBusy" in payload || "canSubmitScore" in payload) {
        return payload;
    }
    return {
        room: payload,
        isBusy: payload?.isBusy ?? true,
    };
};

const GameNotificationPopup = ({ requests = null, onRequestActionComplete = null, from = null }) => {
    const { t } = useTranslation("common");
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const activeMatch = useSelector(selectDuelsActiveMatch);
    console.log('activeMatch: ', activeMatch);
    const { socket, isReady } = useSocket();
    const userData =
        useSelector((state) => state.user.userData) ||
        JSON.parse(getLocalStorageData("user") || "{}");
    const storeJoinRequests = useSelector(selectJoinRequests);
    const isExternallyControlled = Array.isArray(requests);
    const joinRequests = isExternallyControlled ? requests : storeJoinRequests;
    useJoinRequestSync({ userId: userData?._id, socket, isReady, enabled: !isExternallyControlled });
    console.log('joinRequests: ', joinRequests);

    const getRequestId = (request) => request?._id || request?.requestId;
    const getRequesterName = (request) =>
        request?.requestedBy?.userName ||
        request?.requesterId?.userName ||
        request?.userId?.userName ||
        request?.userName ||
        "User";
    const getRoomName = (request) =>
        request?.roomId?.name ||
        request?.room?.name ||
        request?.roomName ||
        request?.matchId?.roomId?.name ||
        "-";
    const getGameType = (request) => request?.gameTypeId || request?.roomId?.gameTypeId || {};
    const getWaitlistPosition = (request) =>
        request?.position ??
        request?.waitlistPosition ??
        request?.roomId?.waitlistPosition ??
        request?.roomId?.position ??
        null;
    const isWaitlistRequest = (request) =>
        from === "waitlist" ||
        request?.type === "waitlist" ||
        request?.requestType === "waitlist" ||
        request?.waitlistId ||
        getWaitlistPosition(request) !== null;
    const sanitizeUrl = (url = "") => url.replace(/%22$/g, "").trim();
    const getGameLogo = (request) => {
        const logo = sanitizeUrl(getGameType(request)?.logo || "");
        return logo || fifa;
    };
    const getGameName = (request) => getGameType(request)?.name || "-";
    const getGamePlatform = (request) =>
        getGameType(request)?.platform || request?.roomId?.platform || "-";
    const requestTypeHeading = from === "waitlist"
        ? (t("one_to_one.waitlist_requests") || "Waitlist Requests")
        : (t("one_to_one.join_requests") || "Join Requests");

    const navigateToGameRoom = useCallback(
        (rawPayload) => {
            const eventData = normalizeActiveMatchPayload(rawPayload);
            const roomData = eventData?.room;
            const roomId = roomData?._id || null;
            const shouldOpenScoreSubmission =
                eventData?.canSubmitScore === true && eventData?.isBusy === false;

            if (shouldOpenScoreSubmission) {
                const scorePath = "/one-to-one-score-submission";
                if (location.pathname === scorePath) return;
                navigate(scorePath, {
                    state: {
                        data: eventData || {},
                    },
                });
                return;
            }

            if (roomId && eventData?.isBusy) {
                const roomPath = `/one-to-one-message/${roomId}`;
                if (location.pathname === roomPath) return;
                navigate(roomPath, {
                    state: {
                        data: eventData || {},
                    },
                });
            }
        },
        [location.pathname, navigate]
    );

    const handleJoinRequestAction = async (request, action) => {
        console.log('request, action: ', request, action);
        const requestId = getRequestId(request);
        if (!requestId) return;
        let endpoint = "";
        if (from === "waitlist") {
            endpoint = action === "accept"
                ? END_POINTS.APPROVE_WAITLIST_REQUEST
                : END_POINTS.DELETE_WAITLIST_REQUEST;
        } else {
            endpoint = action === "accept"
                ? END_POINTS.ACCEPT_JOIN_REQUEST
                : END_POINTS.DECLINE_JOIN_REQUEST;
        }
        dispatch(showLoader());
        const payload = from === "waitlist" ? { waitlistId: requestId } : { requestId };

        try {
            const response = await postApiRequest(endpoint, payload);
            console.log('response: ', response);
            if (response?.success) {
                if (from === "waitlist") {
                    dispatch(removeJoinRequestById(requestId));
                } else {
                    navigateToGameRoom(response?.room);
                }
                // dispatch(removeJoinRequestById(requestId));
                // showToast(
                //     response?.message ||
                //     (action === "accept"
                //         ? t("one_to_one.request_accepted") || "Request accepted"
                //         : t("one_to_one.request_declined") || "Request declined"),
                //     "success"
                // );
            }
        } catch (error) {
            showToast(
                error?.response?.data?.message ||
                error?.error?.message ||
                (action === "accept"
                    ? "Failed to accept request"
                    : "Failed to decline request"),
                "error"
            );
        } finally {
            if (!isExternallyControlled) {
                dispatch(removeJoinRequestById(requestId));
            }
            onRequestActionComplete?.(requestId);
            dispatch(hideLoader());
        }
    };

    if (!joinRequests?.length) return null;

    return (
        <div className="game-notification-popup-container">
            <h3 className="game-notification-popup-heading">
                {requestTypeHeading}
            </h3>
            {joinRequests?.length > 0 && joinRequests?.map((request, index) => (
                <Card key={getRequestId(request) || index} className="game-notification-popup">
                    <div className="popup-content">
                        <p className="room-platform">
                            {(t("one_to_one.request_type") || "Type")}:{" "}
                            {isWaitlistRequest(request)
                                ? (t("one_to_one.waitlist_request_type") || "Waitlist")
                                : (t("one_to_one.join_request_type") || "Room Join Request")}
                        </p>
                        <h4 className="invited-by">
                            {(t("one_to_one.requested_by") || "REQUESTED BY")} {getRequesterName(request)}
                        </h4>
                        <p className="room-platform" >
                            {(t("one_to_one.room") || "Room")}: {getRoomName(request)}
                        </p>
                        {getWaitlistPosition(request) !== null && (
                            <p className="room-platform">
                                {(t("one_to_one.waitlist_position") || "Waitlist Position")}: {getWaitlistPosition(request)}
                            </p>
                        )}
                        <div className="game-info">
                            <div style={{ display: "flex", gap: "10px" }}>
                                <img
                                    src={getGameLogo(request)}
                                    alt="Game Icon"
                                    className="game-icon"
                                    loading="lazy"
                                />
                                <div className="game-details">
                                    <Title level={5} className="game-title">
                                        {(t("one_to_one.game") || "Game")}: {getGameName(request)}
                                    </Title>
                                    <Text className="platform">
                                        {(t("one_to_one.platform") || "Platform")}: {getGamePlatform(request)}
                                    </Text>
                                </div>
                            </div>
                            <Space className="action-buttons">
                                <Button
                                    className="rdp-action-btn rdp-action-btn--accept"
                                    disabled={activeMatch?.isBusy}
                                    onClick={() => handleJoinRequestAction(request, "accept")}
                                >
                                    ✓ {t("one_to_one.accept")}
                                </Button>
                                <Button
                                    className="rdp-action-btn rdp-action-btn--decline"
                                    onClick={() => handleJoinRequestAction(request, "decline")}
                                >
                                    ✕ {t("one_to_one.decline")}
                                </Button>
                            </Space>
                        </div>
                    </div>
                </Card>
            ))}
        </div>
    );
};

GameNotificationPopup.propTypes = {
    requests: PropTypes.array,
    onRequestActionComplete: PropTypes.func,
    from: PropTypes.string,
};

export default GameNotificationPopup;